import { useState } from 'react';
import { useParams } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';
import { IconButton } from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import {
  Document_FieldsFragment,
  DocumentGenerationTemplateType,
  FileExportType,
  useDocument_DownloadFilesMutation,
  useDocument_MarkDocumentAsSignedMutation,
  useDocumentGeneration_GenerateDocumentForEstateWorkItemMutation,
} from 'queries';

import Spinner from 'common/spinner';
import DocumentDeleteModal from 'pages/common/document/document-delete-modal';
import DocumentEditModal from 'pages/common/document/document-edit-modal';
import DocumentShareModal from 'pages/common/document/document-share-modal';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  menu: {
    padding: '16px 0',
    borderRadius: '8px',
    border: '1px solid #ECEEF1',
    boxShadow: ' 0px 20px 24px -4px rgba(0, 0, 0, 0.08), 0px 8px 8px -4px rgba(0, 0, 0, 0.03)',
    backgroundColor: '#fff',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    justifyContent: 'flex-start',
    textAlign: 'left',
  },
  redText: {
    color: '#9E2B25',
  },
}));

type Props = {
  document: Document_FieldsFragment;
  onRefreshDocuments?: () => void;
  isDocumentsForSignatureTab?: boolean;
};

const MoreMenu = ({ document, onRefreshDocuments, isDocumentsForSignatureTab }: Props) => {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRefreshDocuments = () => {
    if (onRefreshDocuments) {
      onRefreshDocuments();
    }
  };

  const [downloadFiles, { loading: downloadFilesExecuting }] = useDocument_DownloadFilesMutation({
    variables: {
      ids: [document.id],
      portalId,
    },
    onCompleted: ({ data: downloadFilesData }) => {
      window.open(downloadFilesData[0].signedUrl, '_blank');

      handleClose();
    },
    onError: (e) => {
      showAlert({
        message: 'Error downloading files: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });

      handleClose();
    },
  });

  const [markDocumentAsSigned, { loading: markDocumentAsSignedExecuting }] =
    useDocument_MarkDocumentAsSignedMutation({
      variables: {
        input: {
          portalId,
          documentId: document.id,
        },
      },
      onCompleted: handleRefreshDocuments,
      onError: (e) => {
        showAlert({
          message: 'Error downloading files: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });

        handleClose();
      },
    });

  const [
    generateDocumentForEstateWorkItem,
    { loading: generateDocumentForEstateWorkItemExecuting },
  ] = useDocumentGeneration_GenerateDocumentForEstateWorkItemMutation({
    onCompleted: ({ data }) => {
      try {
        const link = window.document.createElement('a');
        link.href = `data:${data.mimeType};base64,${data.contentBase64}`;
        link.download = data.fileName;
        link.click();

        showToast({
          message: 'PDF exported successfully!',
          severity: NotificationSeverity.SUCCESS,
        });
      } catch (error) {
        showAlert({
          message: 'Error exporting PDF',
          severity: NotificationSeverity.ERROR,
        });
      }

      handleClose();
    },
    onError: (e) => {
      showAlert({
        message: 'Error generating document for estate work item: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });

      handleClose();
    },
  });

  const getMenuItems = () => {
    const menuItems = [];

    menuItems.push({
      label: 'Download document',
      icon: <DownloadIcon fontSize="small" />,
      onClick: () => downloadFiles(),
    });

    if (
      document.estateWorkItemId &&
      document.documentGenerationTemplate &&
      document.documentGenerationTemplate.type === DocumentGenerationTemplateType.Letter
    ) {
      menuItems.push({
        label: 'Export to PDF',
        icon: <DownloadIcon fontSize="small" />,
        onClick: () =>
          generateDocumentForEstateWorkItem({
            variables: {
              input: {
                portalId,
                documentGenerationTemplateId: document.documentGenerationTemplateId!,
                estateWorkItemId: document.estateWorkItemId!,
                keepEmptyPlaceholders: Boolean(document.documentGenerationKeepEmptyPlaceholders),
                saveToVault: false,
                updateStatuses: false,
                fileExportType: FileExportType.Pdf,
              },
            },
          }),
      });
    }

    if (isDocumentsForSignatureTab) {
      menuItems.push({
        label: 'Mark as signed',
        icon: <TaskOutlinedIcon fontSize="small" />,
        onClick: () => markDocumentAsSigned(),
      });
    }

    // menuItems.push({
    //   label: 'Share document',
    //   icon: <PersonAddAlt1Icon fontSize="small" />,
    //   onClick: () => setShowShareModal(true),
    // });

    menuItems.push({
      label: 'Edit document tag',
      icon: <EditIcon fontSize="small" />,
      onClick: () => setShowEditModal(true),
    });

    menuItems.push({
      label: 'Delete document',
      icon: <DeleteIcon fontSize="small" />,
      onClick: () => setShowDeleteModal(true),
    });

    return menuItems;
  };

  if (
    downloadFilesExecuting ||
    generateDocumentForEstateWorkItemExecuting ||
    markDocumentAsSignedExecuting
  ) {
    return <Spinner />;
  }

  return (
    <>
      <DocumentShareModal
        showModal={showShareModal}
        setShowModal={setShowShareModal}
        selected={[document]}
      />
      <DocumentDeleteModal
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        documentIds={[document.id]}
        portalId={portalId}
        onDeleteSuccess={handleRefreshDocuments}
      />
      <DocumentEditModal
        showModal={showEditModal}
        setShowModal={setShowEditModal}
        document={document}
        portalId={portalId}
        onEditComplete={() => {
          setShowEditModal(false);
          if (onRefreshDocuments) {
            onRefreshDocuments();
          }
        }}
      />
      <IconButton
        id="demo-customized-button"
        aria-controls={open ? 'demo-customized-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
      >
        <MoreHorizIcon />
      </IconButton>
      <Menu
        PaperProps={{
          elevation: 0,
          className: classes.menu,
        }}
        id="demo-customized-menu"
        MenuListProps={{
          'aria-labelledby': 'demo-customized-button',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {getMenuItems().map((item) => (
          <MenuItem
            key={item.label}
            onClick={item.onClick}
            disableRipple
            className={clsx(classes.menuItem, {
              [classes.redText]: item.label === 'Delete document',
            })}
          >
            {item.icon}
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default MoreMenu;
